import { DeliveryTrack } from './delivery-tracking.models';

/**
 * Pure state → copy mapping for the customer tracking UI. Kept free of Angular
 * so it can be unit tested directly.
 */

export type BannerTone = 'calm' | 'active' | 'arrived' | 'success' | 'problem';

export interface DeliveryBanner {
  tone: BannerTone;
  headline: string;
  detail: string;
  /** One-line version of headline and detail. */
  text: string;
  /** Avatar letter; only set while a driver is relevant. */
  initial: string | null;
}

export type StepStatus = 'done' | 'current' | 'upcoming' | 'failed';

export interface DeliveryStep {
  key: 'received' | 'preparing' | 'ready' | 'out' | 'delivered';
  label: string;
  status: StepStatus;
  /** Formatted in the store's timezone, '' when unknown. */
  time: string;
}

export interface ViewOptions {
  /** IANA timezone of the store, e.g. America/New_York. */
  timeZone?: string | null;
  /** False while the store has not yet accepted the order. */
  orderAccepted?: boolean;
  now?: Date;
}

export function formatTime(iso: string | null | undefined, timeZone?: string | null): string {
  if (!iso) {
    return '';
  }
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return '';
  }
  const options: Intl.DateTimeFormatOptions = { hour: 'numeric', minute: '2-digit' };
  try {
    return new Intl.DateTimeFormat('en-US', timeZone ? { ...options, timeZone } : options).format(date);
  } catch (e) {
    // Unknown timezone name: fall back to the device's timezone.
    return new Intl.DateTimeFormat('en-US', options).format(date);
  }
}

/** Whole minutes until arrival, or null when the backend gave no estimate. */
export function etaMinutes(track: DeliveryTrack, now: Date = new Date()): number | null {
  if (typeof track.eta_minutes === 'number' && isFinite(track.eta_minutes)) {
    return Math.max(0, Math.round(track.eta_minutes));
  }
  if (track.eta_at) {
    const eta = new Date(track.eta_at).getTime();
    if (!isNaN(eta)) {
      return Math.max(0, Math.ceil((eta - now.getTime()) / 60000));
    }
  }
  return null;
}

export function etaText(minutes: number | null): string {
  if (minutes === null) {
    return 'Arriving soon';
  }
  if (minutes < 1) {
    return 'Arriving any moment';
  }
  return `Arriving in about ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
}

function join(headline: string, detail: string, separator = ' · '): string {
  if (!detail) {
    return headline;
  }
  return `${headline}${separator}${detail.charAt(0).toLowerCase()}${detail.slice(1)}`;
}

export function buildBanner(track: DeliveryTrack, options: ViewOptions = {}): DeliveryBanner {
  const name = track.driver && track.driver.first_name ? track.driver.first_name : null;
  const initial = track.driver ? track.driver.initial || (name ? name.charAt(0).toUpperCase() : null) : null;
  const times = track.times || {};
  const tz = options.timeZone;

  const make = (tone: BannerTone, headline: string, detail: string, showDriver: boolean, separator?: string) => ({
    tone,
    headline,
    detail,
    text: join(headline, detail, separator),
    initial: showDriver ? initial : null,
  });

  switch (track.state) {
    case 'EN_ROUTE':
      return make(
        'active',
        name ? `${name} is on the way` : 'Your driver is on the way',
        etaText(etaMinutes(track, options.now)),
        true
      );

    case 'ARRIVED': {
      const at = formatTime(times.arrived_at, tz);
      return make(
        'arrived',
        name ? `${name} has arrived` : 'Your driver has arrived',
        at ? `Arrived at ${at}` : 'Your order is at the door',
        true
      );
    }

    case 'DELIVERED': {
      const at = formatTime(times.delivered_at, tz);
      return make('success', at ? `Delivered at ${at}` : 'Delivered', 'Enjoy your order!', false);
    }

    case 'FAILED':
      return make('problem', 'We couldn’t complete your delivery', 'Call the store', false, ' — ');

    case 'CANCELLED':
      return make('problem', 'This delivery was cancelled', 'Call the store if you have questions', false, ' — ');

    case 'PENDING':
    case 'ASSIGNED':
    default: {
      const assigned = track.state === 'ASSIGNED' && !!name;
      if (times.ready_at) {
        return make(
          'calm',
          'Your order is ready',
          assigned ? `${name} will be on the way soon` : 'Waiting for a driver',
          assigned
        );
      }
      return make(
        'calm',
        'Your order is being prepared',
        assigned ? `${name} will deliver it when it’s ready` : 'We’ll find a driver when it’s ready',
        assigned
      );
    }
  }
}

export function buildSteps(track: DeliveryTrack, options: ViewOptions = {}): DeliveryStep[] {
  const times = track.times || {};
  const tz = options.timeZone;
  const accepted = options.orderAccepted !== false;
  const state = track.state;

  const dispatched = state === 'EN_ROUTE' || state === 'ARRIVED' || state === 'DELIVERED' || !!times.dispatched_at;
  const delivered = state === 'DELIVERED';
  const stopped = state === 'FAILED' || state === 'CANCELLED';
  // Anything that left the store was ready, whether or not ready_at was recorded.
  const ready = !!times.ready_at || dispatched;
  const preparing = ready || accepted;

  // Index of the phase the order is in right now.
  let current: number;
  if (delivered) {
    current = 5; // everything done
  } else if (dispatched) {
    current = 3;
  } else if (ready) {
    current = 2;
  } else if (preparing) {
    current = 1;
  } else {
    current = 0;
  }

  const statusAt = (index: number): StepStatus => {
    if (index < current) {
      return 'done';
    }
    if (index === current) {
      return 'current';
    }
    return 'upcoming';
  };

  const steps: DeliveryStep[] = [
    { key: 'received', label: accepted || ready ? 'Received' : 'Submitting', status: statusAt(0), time: formatTime(times.placed_at, tz) },
    { key: 'preparing', label: 'Preparing', status: statusAt(1), time: '' },
    { key: 'ready', label: 'Ready', status: statusAt(2), time: formatTime(times.ready_at, tz) },
    { key: 'out', label: 'Out for delivery', status: statusAt(3), time: formatTime(times.dispatched_at, tz) },
    { key: 'delivered', label: 'Delivered', status: statusAt(4), time: formatTime(times.delivered_at, tz) },
  ];

  if (stopped) {
    // A stopped delivery has no step in progress: show what happened, then the outcome.
    const happened = [true, preparing, ready, dispatched];
    steps.forEach((step, index) => {
      if (index < 4) {
        step.status = happened[index] ? 'done' : 'upcoming';
      }
    });
    steps[4].status = 'failed';
    steps[4].label = state === 'CANCELLED' ? 'Cancelled' : 'Not delivered';
    steps[4].time = '';
  }
  return steps;
}

/** Digits (and a leading +) only, for tel: links. '' when there is nothing dialable. */
export function telHref(phone: string | null | undefined): string {
  if (!phone) {
    return '';
  }
  const trimmed = String(phone).trim();
  const digits = trimmed.replace(/\D/g, '');
  if (digits.length < 7) {
    return '';
  }
  return `tel:${trimmed.startsWith('+') ? '+' : ''}${digits}`;
}

export function formatPhone(phone: string | null | undefined): string {
  if (!phone) {
    return '';
  }
  const digits = String(phone).replace(/\D/g, '');
  const local = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (local.length === 10) {
    return `(${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;
  }
  return String(phone).trim();
}

/** "Updated 12s ago" for the map; '' when the timestamp is unknown. */
export function updatedAgo(recordedAt: string | null | undefined, now: Date = new Date()): string {
  if (!recordedAt) {
    return '';
  }
  const at = new Date(recordedAt).getTime();
  if (isNaN(at)) {
    return '';
  }
  const seconds = Math.max(0, Math.round((now.getTime() - at) / 1000));
  if (seconds < 5) {
    return 'Updated just now';
  }
  if (seconds < 60) {
    return `Updated ${seconds}s ago`;
  }
  const minutes = Math.floor(seconds / 60);
  return `Updated ${minutes} min ago`;
}
